import React, { useEffect, useState, useRef, useMemo } from "react";
import { FaWhatsapp, FaInstagram, FaFacebook, FaTiktok, FaMapMarkerAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet, HelmetProvider } from 'react-helmet-async';

const ENDPOINT_API = "https://www.perfumarinha.pt/api";
const ENDPOINT_FOTOS_BASE = "https://www.perfumarinha.pt";

const STATIC_ICONS = {
  whatsapp: <FaWhatsapp size={36} className="text-white" />,
  instagram: <FaInstagram size={36} className="text-white" />,
  facebook: <FaFacebook size={36} className="text-white" />,
  tiktok: <FaTiktok size={36} className="text-white" />,
  googlemaps: <FaMapMarkerAlt size={36} className="text-white" />,
};

const STATIC_LABEL_REDES = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  googlemaps: "Google Maps"
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const Loader = React.memo(() => (
  <HelmetProvider>
    <Helmet><title>Carregando... PerfuMarinha</title></Helmet>
    <div className="min-h-screen flex flex-col justify-center items-center bg-brand-light-pink text-brand-primary font-serif p-4">
      <svg className="animate-spin h-10 w-10 text-brand-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p className="text-2xl tracking-wider">Carregando com carinho...</p>
    </div>
  </HelmetProvider>
));

const ErrorDisplay = React.memo(() => (
  <HelmetProvider>
    <Helmet><title>Erro - PerfuMarinha</title></Helmet>
    <div className="min-h-screen flex flex-col justify-center items-center bg-red-50 text-red-700 p-6 text-center font-sans">
      <h1 className="text-3xl font-bold mb-3 font-serif">Oops! Algo deu errado.</h1>
      <p className="text-lg">Não foi possível carregar as informações da loja.</p>
      <p className="text-lg">Por favor, tente novamente mais tarde.</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-6 px-6 py-2 bg-brand-primary text-white rounded-lg font-semibold hover:bg-brand-secondary transition-colors"
      >
        Tentar Novamente
      </button>
    </div>
  </HelmetProvider>
));

export default function App() {
  const [data, setData] = useState(null);
  const [modalImg, setModalImg] = useState(null);
  const [modalAlt, setModalAlt] = useState("");
  const [showCatalogoModal, setShowCatalogoModal] = useState(false);
  const tapTimeout = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [tapCount, setTapCount] = useState(0);

  const handleSecretTap = React.useCallback(() => {
    setTapCount(currentCount => {
      const newCount = currentCount + 1;
      if (newCount >= 3) {
        window.location.href = `${ENDPOINT_FOTOS_BASE}/admin/`;
        return 0;
      }
      if (tapTimeout.current) {
        clearTimeout(tapTimeout.current);
      }
      tapTimeout.current = setTimeout(() => {
        setTapCount(0);
      }, 800);
      return newCount;
    });
  }, []);

  useEffect(() => {
    fetch(`${ENDPOINT_API}/get_site_data.php?v=${Date.now()}`)
      .then(res => {
        if (!res.ok) {
          return res.text().then(text => {
            throw new Error(`Erro HTTP ${res.status}: ${text || res.statusText}`);
          });
        }
        return res.json();
      })
      .then(fetchedData => {
        if (typeof fetchedData !== 'object' || fetchedData === null) {
          throw new Error("Formato de dados inválido do servidor.");
        }
        const normalizedData = {
          logo: fetchedData.logo || "",
          titulo: fetchedData.titulo || "PerfuMarinha",
          descricao: fetchedData.descricao || "Sua loja de perfumes e cosméticos.",
          catalogoUrl: fetchedData.catalogoUrl || "", // <-- NOVO
          redesSociais: (typeof fetchedData.redesSociais === 'object' && fetchedData.redesSociais !== null) ? fetchedData.redesSociais : {},
          secoes: Array.isArray(fetchedData.secoes) ? fetchedData.secoes : []
        };
        setData(normalizedData);
      })
      .catch(() => {
        setData(false);
      });

    const currentTapTimeoutRef = tapTimeout.current;
    return () => {
      if (currentTapTimeoutRef) {
        clearTimeout(currentTapTimeoutRef);
      }
    };
  }, []);

  const openImageModal = React.useCallback((imageUrl, altText) => {
    setModalImg(imageUrl);
    setModalAlt(altText || "Imagem do Produto Ampliada");
    document.body.style.overflow = 'hidden';
  }, []);

  const closeImageModal = React.useCallback(() => {
    setModalImg(null);
    setModalAlt("");
    document.body.style.overflow = 'auto';
  }, []);

  const openCatalogoModal = React.useCallback(() => {
    setShowCatalogoModal(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeCatalogoModal = React.useCallback(() => {
    setShowCatalogoModal(false);
    document.body.style.overflow = 'auto';
  }, []);

  const pageMeta = useMemo(() => {
    if (!data) return { pageTitle: "Carregando...", pageDescription: "Carregando descrição...", ogImage: `${ENDPOINT_FOTOS_BASE}/default-og-image.png` };
    return {
      pageTitle: data.titulo || "PerfuMarinha - Beleza que Encanta",
      pageDescription: data.descricao ? data.descricao.replace(/\n/g, ' ') : "Cosméticos, Perfumes e Autocuidado.",
      ogImage: data.logo ? `${ENDPOINT_FOTOS_BASE}/fotos/${data.logo}` : `${ENDPOINT_FOTOS_BASE}/default-og-image.png`
    };
  }, [data]);

  if (data === null) return <Loader />;
  if (data === false) return <ErrorDisplay />;

  // NOVO: Catálogo vindo do admin (com fallback)
  const defaultCatalogoUrl = "https://www.catalogoboticario.com/portugal-boti-2025/ciclo-12/catalogo-12/?page=1";
  const catalogoBoticarioUrl =
    (data?.catalogoUrl && data.catalogoUrl.trim())
      ? data.catalogoUrl.trim()
      : defaultCatalogoUrl;

  return (
    <HelmetProvider>
      <Helmet>
        <html lang="pt" />
        <title>{pageMeta.pageTitle}</title>
        <meta name="description" content={pageMeta.pageDescription} />
        <meta property="og:title" content={pageMeta.pageTitle} />
        <meta property="og:description" content={pageMeta.pageDescription} />
        <meta property="og:image" content={pageMeta.ogImage} />
        <meta property="og:url" content="https://www.perfumarinha.pt" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.perfumarinha.pt" />
      </Helmet>

      <div className="min-h-screen bg-brand-light-pink text-brand-text font-sans flex flex-col items-center relative overflow-x-hidden">
        <AnimatePresence>
          {modalImg && (
            <motion.div
              key="imageModal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
              onClick={closeImageModal} role="dialog" aria-modal="true" aria-labelledby="image-modal-title"
            >
              <motion.div
                onClick={(e) => e.stopPropagation()} className="relative bg-white p-2 rounded-lg shadow-elegant"
                initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <img src={modalImg} alt={modalAlt} id="image-modal-title" className="max-w-[85vw] max-h-[80vh] object-contain rounded-md" />
                <button onClick={closeImageModal} className="absolute -top-3 -right-3 bg-brand-primary text-white rounded-full p-1.5 shadow-md hover:bg-brand-secondary transition-colors" aria-label="Fechar modal de imagem">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </motion.div>
            </motion.div>
          )}

          {showCatalogoModal && (
            <motion.div
              key="catalogoModal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/85 flex flex-col items-center justify-center z-[60] p-4 backdrop-blur-sm"
              onClick={closeCatalogoModal} role="dialog" aria-modal="true" aria-labelledby="catalogo-modal-title"
            >
              <motion.div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-transparent w-full max-w-4xl h-[85vh] sm:h-[90vh] flex flex-col items-center"
                initial={{ y: "50vh", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "50vh", opacity: 0 }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
              >
                <div className="w-full flex justify-end mb-2">
                  <button onClick={closeCatalogoModal} className="bg-white/80 text-black rounded-full p-2 hover:bg-white leading-none" aria-label="Fechar catálogo">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="iframe-container w-full flex-grow rounded-lg overflow-hidden shadow-2xl">
                  <iframe
                    id="catalogo-modal-title"
                    src={catalogoBoticarioUrl}
                    title="Catálogo"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.header
          variants={fadeIn} initial="hidden" animate="visible"
          className="w-full flex flex-col items-center pt-12 pb-10 sm:pt-20 sm:pb-16 px-4 text-center bg-gradient-to-b from-white to-brand-light-pink shadow-sm"
        >
          {data.logo && (
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 120 }}
              className="relative mb-6 w-48 h-auto sm:w-64"
            >
              <img
                src={`${ENDPOINT_FOTOS_BASE}/fotos/${data.logo}?v=${Date.now()}`}
                alt="Logotipo PerfuMarinha"
                className="w-full h-full object-contain drop-shadow-lg"
                width="256" height="128" loading="eager"
              />
            </motion.div>
          )}
          <motion.h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-secondary mb-4 leading-tight">
            {data.titulo}
          </motion.h1>
          <div className="w-20 border-t-2 border-brand-primary rounded mx-auto my-4 sm:my-6" />
          <motion.p className="text-lg sm:text-xl text-brand-text-light max-w-xl mx-auto whitespace-pre-line leading-relaxed">
            {data.descricao}
          </motion.p>
        </motion.header>

        {Object.keys(data.redesSociais).length > 0 && (
          <motion.section
            variants={fadeIn} initial="hidden" animate="visible"
            className="flex flex-wrap justify-center gap-5 sm:gap-6 my-10 sm:my-12 px-4"
            aria-label="Nossas Redes Sociais"
          >
            {Object.entries(data.redesSociais).map(([key, rede]) =>
              (rede && rede.show && rede.url) ? (
                <motion.a
                  key={key} href={rede.url} target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.15, y: -4, rotate: 3 }} whileTap={{ scale: 0.9 }}
                  className={`rounded-full p-3.5 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 ${key === "whatsapp" ? "bg-green-500 hover:bg-green-600" : key === "instagram" ? "bg-pink-500 hover:bg-pink-600" : key === "facebook" ? "bg-blue-600 hover:bg-blue-700" : key === "tiktok" ? "bg-black hover:bg-gray-800" : "bg-red-600 hover:bg-red-700"}`}
                  title={STATIC_LABEL_REDES[key] || key.charAt(0).toUpperCase() + key.slice(1)}
                >
                  {STATIC_ICONS[key]}
                </motion.a>
              ) : null
            )}
          </motion.section>
        )}

        {data.secoes && data.secoes.map((secao, index) => {
          if (secao.tipo === 'produtos' && secao.itens && secao.itens.length > 0) {
            return (
              <motion.div
                key={secao.id || `secao-prod-${index}`}
                variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}
                className="w-full max-w-4xl mx-auto my-12 sm:my-16 rounded-2xl shadow-elegant bg-white/95 p-6 sm:p-8 backdrop-blur-sm"
              >
                <h2 className="font-serif text-3xl sm:text-4xl text-brand-secondary mb-8 sm:mb-10 text-center">
                  {secao.titulo}
                </h2>
                <motion.div
                  variants={staggerContainer} initial="hidden" animate="visible"
                  className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-6 sm:gap-y-8"
                >
                  {secao.itens.map((item, itemIdx) => {
                    const isCatalogoLink = typeof item.link === 'string' && item.link.trim().toUpperCase() === 'IFRAME_CATALOGO_BOTICARIO';

                    return (
                      <motion.div
                        key={item.id_db_item || `item-${itemIdx}`}
                        variants={fadeIn}
                        className="flex flex-col items-center cursor-pointer group transition-all duration-300"
                        onClick={() => {
                          if (isCatalogoLink) {
                            openCatalogoModal();
                          } else if (item.imagem) {
                            openImageModal(`${ENDPOINT_FOTOS_BASE}/fotos/${item.imagem}?v=${Date.now()}`, item.nome);
                          } else if (item.link) {
                            window.open(item.link, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (isCatalogoLink) { openCatalogoModal(); } else if (item.imagem) { openImageModal(`${ENDPOINT_FOTOS_BASE}/fotos/${item.imagem}?v=${Date.now()}`, item.nome); } else if (item.link) { window.open(item.link, '_blank'); } } }}
                        aria-label={`Ver detalhes de ${item.nome || 'produto em destaque'}`}
                      >
                        <div className="w-full p-1 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                          <div className="aspect-square w-full overflow-hidden rounded-lg">
                            {item.imagem ? (
                              <img
                                src={`${ENDPOINT_FOTOS_BASE}/fotos/${item.imagem}?v=${Date.now()}`}
                                alt={item.nome || "Imagem de Destaque"}
                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
                                loading="lazy"
                                width="300"
                                height="300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50 rounded-lg font-serif">Sem Imagem</div>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 text-center">
                          {item.nome && (
                            <h3 className="font-semibold text-sm sm:text-base text-brand-text group-hover:text-brand-primary transition-colors">{item.nome}</h3>
                          )}
                          {item.link && !isCatalogoLink && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:underline mt-1 block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Ver Mais
                            </a>
                          )}
                          {!item.nome && item.imagem && !item.link && (
                            <h3 className="font-semibold text-sm sm:text-base text-brand-text group-hover:text-brand-primary transition-colors">Ver Detalhes</h3>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            );
          } else if (secao.tipo === 'catalogo') {
            return (
              <motion.section
                key={secao.id || `secao-cat-${index}`}
                variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}
                className="w-full max-w-4xl mx-auto my-12 sm:my-16 px-4"
                aria-labelledby={`catalogo-title-${secao.id || index}`}
              >
                <h2
                  id={`catalogo-title-${secao.id || index}`}
                  className="font-serif text-3xl sm:text-4xl text-brand-secondary mb-8 sm:mb-10 text-center"
                >
                  {secao.titulo}
                </h2>
                <div className="iframe-container rounded-lg shadow-elegant bg-white">
                  <iframe
                    src={catalogoBoticarioUrl}
                    title={secao.titulo || "Catálogo"}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <p className="text-center text-sm text-brand-text-light mt-4 px-2">
                  Navegue pelo catálogo oficial. Clique na imagem para interagir.
                </p>
              </motion.section>
            );
          }
          return null;
        })}

        <motion.footer
          variants={fadeIn} initial="hidden" animate="visible" transition={{ delay: 0.5 }}
          className="w-full bg-white mt-12 sm:mt-20 py-8 px-4 text-center border-t border-gray-200"
        >
          <p
            className="text-brand-text-light text-sm cursor-pointer select-none hover:text-brand-primary transition-colors"
            onClick={handleSecretTap} onTouchEnd={handleSecretTap}
            tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSecretTap()}
            role="button" aria-label="Acesso secreto ao painel de administração"
          >
            © {new Date().getFullYear()} PerfuMarinha. Todos os direitos reservados.
          </p>
        </motion.footer>
      </div>
    </HelmetProvider>
  );
}
